import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/common/components/ui/table';
import { useFormatCurrency } from '@/common/hooks/useFormatCurrency';
import { useScroll } from '@/policy/hooks/useScroll';
import { Car, CarRow } from '@/policy/types/PolicyCoverage';
import { ValidationResult } from '@/policy/types/ValidationResult';

import { ScrollShadows } from '../ScrollShadows/ScrollShadows';
import { ValidableSection } from '../Validation/ValidableSection';

interface MultiCarsSectionProps {
  cars: Car[];
  carIdentifier: string;
  validation: ValidationResult;
  onValidationChange: (validation: ValidationResult) => void;
  isSelected: boolean;
  onTableClick: (sectionUuid: string) => void;
}

type ChapterType = 'A' | 'B' | 'FAQ';

interface ChapterConfig {
  type: ChapterType;
  color: string;
  hasSubGroups: boolean;
}

interface SectionGroup {
  chapter: ChapterType;
  subGroup: string;
  title: string;
  sections: CarRow[];
}

const CHAPTER_CONFIGS: Record<ChapterType, ChapterConfig> = {
  A: { type: 'A', color: 'var(--color-terre-400)', hasSubGroups: false },
  B: { type: 'B', color: 'var(--color-terra-cotta-400)', hasSubGroups: true },
  FAQ: { type: 'FAQ', color: 'var(--color-argile-400)', hasSubGroups: true },
};

export const MultiCarsSection = forwardRef<
  HTMLDivElement,
  MultiCarsSectionProps
>(
  (
    {
      cars,
      carIdentifier,
      validation,
      onValidationChange,
      isSelected,
      onTableClick,
    },
    ref,
  ) => {
    const { formatCurrency } = useFormatCurrency();

    const tryFormatCurrency = (value: string): string => {
      if (!value) return '';
      if (value.endsWith('%') || !Number.parseFloat(value)) return value;
      const formatted = formatCurrency(value);
      return formatted ?? value;
    };

    // Classify section into chapter and subgroup
    const classifySection = (
      warranty: string,
    ): { chapter: ChapterType; subGroup: string } => {
      const lower = warranty.toLowerCase();

      // FAQ chapter
      if (
        lower.includes('f.a.q') ||
        lower.includes('remboursement de frais médicaux') ||
        lower.includes('décès') ||
        lower.includes('mutilation')
      ) {
        if (lower.includes('f.a.q')) return { chapter: 'FAQ', subGroup: 'FAQ' };
        if (lower.includes('remboursement'))
          return { chapter: 'FAQ', subGroup: 'Remboursement' };
        if (lower.includes('décès') || lower.includes('mutilation'))
          return { chapter: 'FAQ', subGroup: 'Décès' };
        return { chapter: 'FAQ', subGroup: 'Autre' };
      }

      // Chapitre B
      if (
        lower.startsWith('chapitre b') ||
        lower.startsWith('b2') ||
        lower.startsWith('b3') ||
        lower.startsWith('b4')
      ) {
        const match = warranty.match(/^(B\d+)/i);
        if (match) return { chapter: 'B', subGroup: match[1].toUpperCase() };
        return { chapter: 'B', subGroup: 'Chapitre B' };
      }

      // Chapitre A (default)
      return { chapter: 'A', subGroup: 'A' };
    };

    // Group sections by chapter and subgroup
    const groupSections = (sections: CarRow[]): SectionGroup[] => {
      const groups = new Map<string, SectionGroup>();

      sections.forEach((section) => {
        const { chapter, subGroup } = classifySection(section.warranties);
        const key = `${chapter}-${subGroup}`;

        if (!groups.has(key)) {
          // Transform "Chapitre B" to "B1" in title for B chapter
          const title =
            chapter === 'B' && subGroup === 'Chapitre B'
              ? section.warranties.replace(/^Chapitre B/i, 'B1')
              : section.warranties;

          groups.set(key, {
            chapter,
            subGroup,
            title,
            sections: [],
          });
        }

        groups.get(key)!.sections.push(section);
      });

      return Array.from(groups.values());
    };

    const {
      scrollContainerRef,
      isScrolled,
      dragProps,
      canScrollLeft,
      canScrollRight,
    } = useScroll();

    const firstCarGroups = groupSections(cars[0].sections);

    // Organize groups by chapter
    const groupsByChapter = {
      A: firstCarGroups.filter((g) => g.chapter === 'A'),
      B: firstCarGroups.filter((g) => g.chapter === 'B'),
      FAQ: firstCarGroups.filter(
        (g) => g.chapter === 'FAQ' && g.subGroup !== 'FAQ',
      ),
    };

    // Get main chapter titles
    const getMainTitle = (chapter: ChapterType) => {
      const allGroups = groupSections(cars[0].sections);
      if (chapter === 'A') {
        return (
          allGroups.find(
            (g) =>
              g.chapter === 'A' &&
              g.title.toLowerCase().startsWith('chapitre a'),
          )?.title || 'Chapitre A'
        );
      }
      if (chapter === 'FAQ') {
        return (
          allGroups.find((g) => g.chapter === 'FAQ' && g.subGroup === 'FAQ')
            ?.title || 'Avenants (F.A.Q.)'
        );
      }
      return 'Chapitre B';
    };

    const renderColumnHeaders = (
      groups: SectionGroup[],
      config: ChapterConfig,
      isLast: boolean,
    ) => {
      return groups.flatMap((group, groupIdx) => {
        const isLastGroup = groupIdx === groups.length - 1;
        return group.sections.map((_, sectionIdx) => {
          const isLastSection = sectionIdx === group.sections.length - 1;
          const needsGroupBorder = isLast && isLastGroup && isLastSection;

          return (
            <ColumnHeaderGroup
              key={`${config.type}-${groupIdx}-${sectionIdx}`}
              config={config}
              needsBorder={needsGroupBorder}
            />
          );
        });
      });
    };

    const renderDataCells = (
      carGroups: SectionGroup[],
      chapterType: ChapterType,
      isLastChapter: boolean,
    ) => {
      return carGroups.flatMap((group, groupIdx) => {
        const isLastGroup = groupIdx === carGroups.length - 1;
        return group.sections.map((section, sectionIdx) => {
          const isLastSection = sectionIdx === group.sections.length - 1;
          const needsGroupBorder =
            isLastChapter && isLastGroup && isLastSection;

          return (
            <DataCellGroup
              key={`${chapterType}-${groupIdx}-${sectionIdx}`}
              section={section}
              needsBorder={needsGroupBorder}
              formatValue={tryFormatCurrency}
            />
          );
        });
      });
    };

    return (
      <ValidableSection
        ref={ref}
        className="my-4"
        sectionIdentifier={carIdentifier}
        validation={validation}
        onValidationChange={onValidationChange}
        isSelected={isSelected}
        onTableClick={onTableClick}
      >
        <ScrollShadows
          canScrollLeft={canScrollLeft}
          canScrollRight={canScrollRight}
        >
          <div
            ref={scrollContainerRef}
            {...dragProps}
            className={`w-full overflow-x-auto scrollbar-visible select-none ${dragProps.className}
            [&::-webkit-scrollbar]:h-1.5
            [&::-webkit-scrollbar-track]:bg-transparent
            [&::-webkit-scrollbar-thumb]:bg-warm-gray-400
            [&::-webkit-scrollbar-thumb]:rounded-full`}
          >
            <Table
              noWrapper
              className="min-w-max w-full"
              style={{ tableLayout: 'auto' }}
            >
              <TableHeader>
                {/* Row 1: Main chapter headers */}
                <TableRow>
                  <TableHead
                    rowSpan={3}
                    className="sticky left-0 z-20 rounded-tl-lg border-r border-background min-w-[120px] align-middle font-bold text-center"
                  >
                    Véhicules
                    {isScrolled && (
                      <span
                        aria-hidden
                        className="absolute top-0 bottom-0 left-full w-4 pointer-events-none bg-gradient-to-r from-black/5 to-transparent"
                      />
                    )}
                  </TableHead>
                  {(['A', 'B', 'FAQ'] as const).map((chapter) => {
                    const groups = groupsByChapter[chapter];
                    if (groups.length === 0) return null;

                    const config = CHAPTER_CONFIGS[chapter];
                    const totalSections = groups.reduce(
                      (sum, g) => sum + g.sections.length,
                      0,
                    );
                    const rowSpan = config.hasSubGroups ? 1 : 2;
                    const isLast = chapter === 'FAQ';

                    return (
                      <TableHead
                        key={chapter}
                        colSpan={totalSections * 3}
                        rowSpan={rowSpan}
                        className={`text-center text-background font-bold ${
                          isLast
                            ? 'rounded-tr-lg'
                            : 'border-r border-background'
                        }`}
                        style={{ backgroundColor: config.color }}
                      >
                        {getMainTitle(chapter)}
                      </TableHead>
                    );
                  })}
                </TableRow>

                {/* Row 2: Subgroup headers (only for B and FAQ) */}
                <TableRow>
                  {(['B', 'FAQ'] as const).map((chapter) => {
                    const groups = groupsByChapter[chapter];
                    if (groups.length === 0) return null;

                    const config = CHAPTER_CONFIGS[chapter];
                    const isLast = chapter === 'FAQ';

                    return groups.map((group, idx) => {
                      const isLastGroup = idx === groups.length - 1;
                      return (
                        <TableHead
                          key={`${chapter}-subgroup-${idx}`}
                          colSpan={group.sections.length * 3}
                          className={`text-center text-background font-bold ${
                            isLast && isLastGroup
                              ? ''
                              : 'border-r border-background'
                          }`}
                          style={{ backgroundColor: config.color }}
                        >
                          {group.title}
                        </TableHead>
                      );
                    });
                  })}
                </TableRow>

                {/* Row 3: Column headers (Coverage, Deductible, Premium) */}
                <TableRow>
                  {(['A', 'B', 'FAQ'] as const).map((chapter) => {
                    const groups = groupsByChapter[chapter];
                    if (groups.length === 0) return null;

                    const config = CHAPTER_CONFIGS[chapter];
                    const isLast = chapter === 'FAQ';

                    return renderColumnHeaders(groups, config, isLast);
                  })}
                </TableRow>
              </TableHeader>

              <TableBody>
                {cars.map((car) => {
                  const carGroups = groupSections(car.sections);
                  const carGroupsByChapter = {
                    A: carGroups.filter((g) => g.chapter === 'A'),
                    B: carGroups.filter((g) => g.chapter === 'B'),
                    FAQ: carGroups.filter(
                      (g) => g.chapter === 'FAQ' && g.subGroup !== 'FAQ',
                    ),
                  };

                  return (
                    <TableRow
                      key={car.serialNumber}
                      className="bg-white even:bg-white"
                    >
                      <TableCell className="sticky left-0 z-10 bg-white border-r border-warm-gray-300 font-semibold text-center">
                        {car.carNumber}
                        <br />
                        {isScrolled && (
                          <span
                            aria-hidden
                            className="absolute top-0 bottom-0 left-full w-4 pointer-events-none bg-gradient-to-r from-black/5 to-transparent"
                          />
                        )}
                      </TableCell>

                      {renderDataCells(carGroupsByChapter.A, 'A', false)}
                      {renderDataCells(carGroupsByChapter.B, 'B', false)}
                      {renderDataCells(carGroupsByChapter.FAQ, 'FAQ', true)}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </ScrollShadows>
      </ValidableSection>
    );
  },
);

MultiCarsSection.displayName = 'MultiCarsSection';

// Helper components for rendering repeated patterns
const ColumnHeaderGroup = ({
  config,
  needsBorder,
}: {
  config: ChapterConfig;
  needsBorder: boolean;
}) => {
  const { t } = useTranslation();

  const borderClass = needsBorder
    ? 'border-r border-background'
    : 'border-r border-warm-gray-200';

  return (
    <>
      <TableHead
        className={`border-r border-warm-gray-200 min-w-[100px] font-bold text-right text-background`}
        style={{ backgroundColor: config.color }}
      >
        {t('garantie.montantAssurance')}
      </TableHead>
      <TableHead
        className={`border-r border-warm-gray-200 min-w-20 font-bold text-right text-background`}
        style={{ backgroundColor: config.color }}
      >
        {t('garantie.franchise')}
      </TableHead>
      <TableHead
        className={`min-w-20 font-bold text-right text-background ${borderClass}`}
        style={{ backgroundColor: config.color }}
      >
        {t('garantie.prime')}
      </TableHead>
    </>
  );
};

const DataCellGroup = ({
  section,
  needsBorder,
  formatValue,
}: {
  section: CarRow;
  needsBorder: boolean;
  formatValue: (value: string) => string;
}) => {
  const borderClass = needsBorder
    ? 'border-r border-warm-gray-300'
    : 'border-r border-warm-gray-200';

  return (
    <>
      <TableCell className="text-right border-r border-warm-gray-200 text-sm">
        {formatValue(section.coverageAmount)}
      </TableCell>
      <TableCell className="text-right border-r border-warm-gray-200 text-sm">
        {section.deductible}
      </TableCell>
      <TableCell className={`text-sm text-right ${borderClass}`}>
        {formatValue(section.premium)}
      </TableCell>
    </>
  );
};
